/**
 * 로그인 장바구니를 GE 견적에 반영하는 흐름의 저장소 표시 — #6179 B.
 *
 * ## GE 는 URL 에 `cart` 가 든 요청이 끝나면 장바구니를 다시 견적한다
 *
 * GE 엔진의 요청 감시(`ApiMutationComponent`)가 머천트 설정 정규식 **`/cart/g`** 에 맞는 fetch·XHR 를 보고
 * `GetCheckoutCartInfo` 를 다시 부른다. dev(QA 스토어)·staging(운영 스토어 MID 30001127) 둘 다 `/cart/g` 였다
 * (2026-11-05 실측). 이름이 두 겹이다 — GE 서버의 머천트 설정 응답(`…/configurations/merchant/<id>/client-settings`)
 * 키는 `"RequestInterceptRegExp": "cart"`, 엔진이 그걸 읽어 만든 컴포넌트 속성은 `RequestInterceptorRegex`(`/cart/g`,
 * `GEM.ComponentsManager.instance.GetAllComponent()` 로 확인). 찾을 땐 둘 다 검색한다.
 * dev 장바구니 CA 실측:
 *
 * | 요청 | cart-info 재호출 | GE 합계 |
 * |---|---|---|
 * | `POST /admin-api/api/v1/guest-cart/` (같은 내용) | +2 | — |
 * | `POST …/cart/guest-token/owner/` (claim) | +1 | — |
 * | `PUT …/cart/guest-token/coupon-selection/` optOut=true | +1 | CA$535 → CA$760 |
 * | 같은 PUT optOut=false | +1 | CA$760 → CA$535 |
 * | `GET /robots.txt?x=cart` | +1 | — |
 * | `POST /robots.txt` · `GET /robots.txt?ge-probe=update` · `GET /api/feature-flag/` | 0 | — |
 *
 * 그래서 준비(보존 merge → sync → claim → 쿠폰 선택)의 마지막 요청이 끝나는 순간 GE 가 새 소유자·선택으로 다시
 * 견적한다 — **새로고침이 필요 없다.** 쿠폰 선택을 바꿔 저장해도 같다.
 * ⚠️ GE 문서는 「장바구니 로드·결제 시도에서만」이라 적는다(「Retrieve Checkout Cart Information」). 위 동작은 머천트
 * 설정이다 — GE 담당자 답(2026-11-05): 스토어에 의도적으로 넣은 값이고 Prod 스토어에서도 유지되며, 이렇게 재계산을
 * 일으키는 것이 권장 방식이다. 그래도 GE 가 정규식을 바꾸면 사라진다 — 그때는 결제 버튼의 준비(`prepareGeCheckoutCoupons`)가 결제창 금액을 지키고,
 * 장바구니 첫 화면만 종전(쿠폰 전 가격)으로 돌아간다.
 * ⚠️ 장바구니 밖 페이지에서도 GE 는 로드 때 cart-info 를 부르고 `cart` 요청마다 한 번 더 부른다(홈 실측). 그래서 merge·claim·
 * 첫 선택 저장은 **탭 세션에 한 번**(토큰·회원 조합)만 한다 — `isCartPrepared`. merge 는 mgmt 부수효과(리마인더 플래그 리셋·
 * 이벤트 기록) 때문에도 반복하면 안 된다(`prepareLoggedInCart` 주석). 그래서 merge 완료는 **따로** 표시한다(`isCartMerged`) —
 * claim·선택 저장이 실패해 첫 준비가 다음 로드로 미뤄져도 merge 는 다시 돌지 않는다(#6185 R4).
 *
 * 저장소를 못 쓰면 세션 표시가 남지 않아 페이지마다 다시 준비한다 — claim·선택 저장은 멱등이지만 **merge 도 매 로드 돌아**
 * mgmt 리마인더 플래그 리셋·`CART_MERGED` 기록이 반복된다(#6185 이전 동작과 같은 수준).
 */

const PREPARED_KEY = "bh_ge_cart_prepared"; // sessionStorage — `${cartToken}:${uid}`
const OPT_OUT_KEY = "bh_cart_coupon_opt_out"; // sessionStorage — "1" = 사용 안 함
const CLAIMED_KEY = "bh_cart_claimed_token"; // localStorage — 회원이 claim 한 cart token

function session(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function local(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

const preparedValue = (cartToken: string, uid: string) => `${cartToken}:${uid}`;

/** 이 탭 세션에서 이 토큰·회원 조합의 claim·첫 선택 저장이 끝났나. 못 읽으면 `false`(다시 준비 — 멱등이다). */
export function isCartPrepared(cartToken: string, uid: string): boolean {
  try {
    return session()?.getItem(PREPARED_KEY) === preparedValue(cartToken, uid);
  } catch {
    return false;
  }
}

export function markCartPrepared(cartToken: string, uid: string): void {
  try {
    session()?.setItem(PREPARED_KEY, preparedValue(cartToken, uid));
  } catch {
    /* 못 남기면 다음 페이지에서 다시 준비한다 */
  }
}

const MERGED_KEY = "bh_ge_cart_merged"; // sessionStorage — `${cartToken}:${uid}`

/** 이 탭 세션에서 이 토큰·회원의 보존 merge 가 끝났나. claim 성공과 무관하다 — merge 는 반복하면 부수효과가 쌓인다. */
export function isCartMerged(cartToken: string, uid: string): boolean {
  try {
    return session()?.getItem(MERGED_KEY) === preparedValue(cartToken, uid);
  } catch {
    return false;
  }
}

export function markCartMerged(cartToken: string, uid: string): void {
  try {
    session()?.setItem(MERGED_KEY, preparedValue(cartToken, uid));
  } catch {
    /* 못 남기면 다음 첫 준비에서 다시 merge 한다 */
  }
}

/**
 * 「사용 안 함」 선택 — **탭 세션 동안만** 유지한다. 서버(`couponOptOut`)에 저장해 GE 견적이 따르므로, 페이지를
 * 옮길 때마다 화면만 `auto` 로 돌아가면 화면(쿠폰 적용)과 GE 합계(쿠폰 없음)가 갈린다. 새 탭·새 세션은 `auto` 로
 * 시작하고 첫 준비에서 서버도 `auto` 로 맞춘다 — `cart-coupon-store.ts` 의 「오래 남아 할인을 조용히 잃지 않게」
 * 취지는 그대로다.
 */
export function readStoredOptOut(): boolean {
  try {
    return session()?.getItem(OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

export function storeOptOut(optOut: boolean): void {
  try {
    if (optOut) session()?.setItem(OPT_OUT_KEY, "1");
    else session()?.removeItem(OPT_OUT_KEY);
  } catch {
    /* 못 남기면 다음 페이지는 auto — 첫 준비가 아니므로 서버는 「사용 안 함」으로 남는다(결제 버튼이 다시 맞춘다) */
  }
}

/** 이 브라우저가 이미 claim 한 토큰인가 — 로그인 사용자의 sync 가 새 토큰을 만든 직후 claim 할지 정한다. */
export function isRememberedClaimedToken(cartToken: string): boolean {
  try {
    return local()?.getItem(CLAIMED_KEY) === cartToken;
  } catch {
    return false;
  }
}

export function rememberClaimedCartToken(cartToken: string): void {
  try {
    local()?.setItem(CLAIMED_KEY, cartToken);
  } catch {
    /* 못 남기면 로그아웃 뒤 토큰 교체를 못 한다 — 종전 동작(결제 버튼 claim 뒤에도 교체 없음)과 같다 */
  }
}

/**
 * 게스트로 확정됐을 때, 지금 토큰이 회원이 claim 한 토큰이면 그 토큰을 돌려준다(= 바꿔야 한다). 아니면 `null`.
 * 돌려주면서 기억을 지운다 — 같은 토큰을 두 번 바꾸지 않는다.
 */
export function claimedTokenToRotate(
  currentCartToken: string | null,
): string | null {
  const l = local();
  if (!l || !currentCartToken) return null;
  try {
    if (l.getItem(CLAIMED_KEY) !== currentCartToken) return null;
    l.removeItem(CLAIMED_KEY);
    return currentCartToken;
  } catch {
    return null;
  }
}
